import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Plane, Menu, X, Globe, User } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

// Routes that render the Travelpayouts White Label. The WL is a self-
// mounting third-party bundle that only hydrates on script execution, so
// SPA navigation between these routes would leave an un-hydrated widget.
// Adding `reloadDocument` on their nav links makes the browser do a full
// document load, which is the officially supported way to embed the WL
// on multiple routes without maintaining duplicate loaders.
const TP_WL_ROUTES = new Set(['/', '/flights']);
const isTpwlRoute = (to) => TP_WL_ROUTES.has(to);

const navItems = [
  { to: '/flights', label: 'Flights' },
  { to: '/hotels', label: 'Hotels' },
  { to: '/cars', label: 'Cars' },
  { to: '/deals', label: 'Deals' },
  { to: '/destinations', label: 'Destinations' },
  { to: '/blog', label: 'Travel Tips' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <Link to="/" reloadDocument className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-xl bg-teal-700 flex items-center justify-center text-white shadow-sm group-hover:rotate-[-8deg] transition-transform">
            <Plane className="w-5 h-5" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-slate-900 text-lg">Cheapora</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 -mt-0.5">By Waan Travel</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              reloadDocument={isTpwlRoute(n.to)}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? 'text-teal-700 bg-teal-50' : 'text-slate-700 hover:text-teal-700 hover:bg-slate-50'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 text-slate-700">
                <Globe className="w-4 h-4" /> SGD
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Currency</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>SGD – Singapore Dollar</DropdownMenuItem>
              <DropdownMenuItem>USD – US Dollar</DropdownMenuItem>
              <DropdownMenuItem>MYR – Malaysian Ringgit</DropdownMenuItem>
              <DropdownMenuItem>IDR – Indonesian Rupiah</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => navigate('/about')}>
            <User className="w-4 h-4" /> Sign in
          </Button>
          <Button size="sm" className="bg-teal-700 hover:bg-teal-800 text-white" onClick={() => navigate('/deals')}>
            View Deals
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[85%] sm:w-96">
            <div className="flex flex-col gap-1 mt-8">
              {navItems.map((n) => (
                <NavLink key={n.to} to={n.to}
                  reloadDocument={isTpwlRoute(n.to)}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `px-3 py-3 rounded-md text-base font-medium ${isActive ? 'bg-teal-50 text-teal-700' : 'text-slate-800 hover:bg-slate-50'}`}>
                  {n.label}
                </NavLink>
              ))}
              <div className="h-px bg-slate-200 my-3" />
              <Button className="bg-teal-700 hover:bg-teal-800 text-white w-full" onClick={() => { setOpen(false); navigate('/deals'); }}>
                View Deals
              </Button>
              <Button variant="outline" className="w-full mt-2" onClick={() => { setOpen(false); navigate('/about'); }}>
                Sign in
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
